import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react'; // QR code library
import { FaPrint, FaDownload, FaHome, FaShare } from 'react-icons/fa';

const OnlineReceipt = () => {
  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    try {
      const transactionData = JSON.parse(localStorage.getItem('lastTransaction'));
      if (!transactionData) {
        navigate('/pos');
        return;
      }
      setTransaction(transactionData);
    } catch (error) {
      console.error('Error loading transaction data:', error);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    alert('PDF download functionality will be implemented');
  };

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Receipt',
          text: `Receipt for purchase on ${transaction?.transactionDate}`,
          url: window.location.href
        });
      } else {
        alert('Sharing is not supported on this browser');
      }
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  if (loading) {
    return (
      <div className="loading-spinner">
        <div className="spinner"></div>
        <p>Loading receipt...</p>
      </div>
    );
  }

  if (!transaction) {
    return (
      <div className="error-container">
        <h2>No receipt data found</h2>
        <button onClick={() => navigate('/pos')} className="btn btn-primary">
          Return to POS
        </button>
      </div>
    );
  }

  return (
    <div className="receipt-container">
      <style>
        {`
          @media print {
            .no-print {
              display: none !important;
            }
            .receipt-content {
              padding: 0;
              box-shadow: none;
            }
          }
          
          .receipt-container {
            max-width: 800px;
            margin: 2rem auto;
            padding: 1rem;
          }
          
          .receipt-content {
            background: white;
            padding: 2rem;
            border-radius: 10px;
            box-shadow: 0 0 20px rgba(0,0,0,0.1);
          }
          
          .receipt-header {
            text-align: center;
            border-bottom: 2px solid #eee;
            padding-bottom: 1rem;
            margin-bottom: 2rem;
          }
          
          .receipt-table {
            width: 100%;
            border-collapse: collapse;
            margin: 1rem 0;
          }
          
          .receipt-table th,
          .receipt-table td {
            padding: 0.5rem;
            border-bottom: 1px solid #eee;
            text-align: left;
          }
          
          .receipt-footer {
            text-align: center;
            margin-top: 2rem;
            padding-top: 1rem;
            border-top: 2px solid #eee;
          }
          
          .action-buttons {
            display: flex;
            gap: 1rem;
            justify-content: center;
            margin-bottom: 1rem;
          }
          
          .btn {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.5rem 1rem;
            border: none;
            border-radius: 5px;
            cursor: pointer;
            transition: all 0.3s ease;
          }
          
          .btn-primary {
            background: #007bff;
            color: white;
          }
          
          .btn:hover {
            transform: translateY(-2px);
            box-shadow: 0 2px 5px rgba(0,0,0,0.2);
          }
          
          .qr-code {
            text-align: center;
            margin-top: 1rem;
          }
        `}
      </style>

      <div className="action-buttons no-print">
        <button onClick={handlePrint} className="btn btn-primary">
          <FaPrint /> Print
        </button>
        <button onClick={handleDownloadPDF} className="btn btn-primary">
          <FaDownload /> Download PDF
        </button>
        <button onClick={handleShare} className="btn btn-primary">
          <FaShare /> Share
        </button>
        <button onClick={() => navigate('/pos')} className="btn btn-primary">
          <FaHome /> Back to POS
        </button>
      </div>

      <div className="receipt-content">
        <div className="receipt-header">
          <h1>REMS ANYTHING</h1>
          <p>Branch: {transaction.branchName}</p>
          <p>Transaction ID: {transaction.shiftId}</p>
          <p>Date: {formatDate(transaction.transactionDate)}</p>
          <p>Time: {transaction.transactionTime}</p>
          <p>Cashier: {transaction.username}</p>
        </div>

        <div className="customer-details">
          <h3>Customer Details</h3>
          <p>Name: {transaction.customerName || 'Walk-in Customer'}</p>
          {transaction.billingAddress && (
            <>
              <p>Address: {transaction.billingAddress}</p>
              <p>City: {transaction.customerCity}</p>
              <p>Country: {transaction.customerCountry}</p>
            </>
          )}
        </div>

        <table className="receipt-table">
          <thead>
            <tr>
              <th>Item</th>
              <th>Qty</th>
              <th>Price</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {transaction.selectedGoods.map((item, index) => (
              <tr key={index}>
                <td>{item.product_name}</td>
                <td>{item.quantity}</td>
                <td>{transaction.currency} {item.selling_price.toFixed(2)}</td>
                <td>{transaction.currency} {(item.quantity * item.selling_price).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="receipt-summary">
          <div style={{ display: 'flex', justifyContent: 'space-between', margin: '0.5rem 0' }}>
            <span>Subtotal:</span>
            <span>{transaction.currency} {(transaction.totalAmount / (1 + transaction.vat)).toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', margin: '0.5rem 0' }}>
            <span>VAT ({(transaction.vat * 100).toFixed()}%):</span>
            <span>{transaction.currency} {(transaction.totalAmount - (transaction.totalAmount / (1 + transaction.vat))).toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', margin: '0.5rem 0', fontWeight: 'bold' }}>
            <span>Total:</span>
            <span>{transaction.currency} {transaction.totalAmount.toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', margin: '0.5rem 0' }}>
            <span>Amount Paid:</span>
            <span>{transaction.currency} {parseFloat(transaction.amountGiven).toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', margin: '0.5rem 0' }}>
            <span>Change:</span>
            <span>{transaction.currency} {transaction.change.toFixed(2)}</span>
          </div>
        </div>

        <div className="receipt-footer">
          <p>Thank you for your business!</p>
          <p>Please keep this receipt for your records</p>
          <div className="qr-code">
            <QRCodeSVG 
              value={JSON.stringify({
                receiptId: transaction.shiftId,
                date: transaction.transactionDate,
                amount: transaction.totalAmount
              })} 
              size={128}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnlineReceipt;